import type { SubscriptionStatus } from 'src/hooks/useSubscription';
import { INVENTORY_ITEM_CATEGORY_ID_TYPES } from 'src/types/itemInventory';

export type SubscriptionAction =
  | 'new'
  | 'upgrade'
  | 'downgrade'
  | 'renew'
  | 'alreadySubscribed'
  | 'subscribedOnApp'
  | 'alreadyOnHigherPlan';

const PAYMENT_MODAL_ACTIONS = ['new', 'upgrade', 'downgrade'] as const;
const MANAGE_ACTIONS = ['renew'] as const;
const DISABLED_ACTIONS = ['alreadySubscribed', 'subscribedOnApp', 'alreadyOnHigherPlan'] as const;

export const isPaymentModalAction = (action: SubscriptionAction): boolean =>
  (PAYMENT_MODAL_ACTIONS as readonly string[]).includes(action);

export const isManageAction = (action: SubscriptionAction): boolean =>
  (MANAGE_ACTIONS as readonly string[]).includes(action);

export const isDisabledAction = (action: SubscriptionAction): boolean =>
  (DISABLED_ACTIONS as readonly string[]).includes(action);

interface Params {
  selectedPlan: string;
  subscriptionStatus: SubscriptionStatus;
}

export const getSubscriptionAction = ({
  selectedPlan,
  subscriptionStatus,
}: Params): SubscriptionAction => {
  const {
    hasAppSubscription,
    hasWebCoreSubscription,
    hasWebMaxSubscription,
    isWebCoreRenewOff,
    isWebMaxRenewOff,
  } = subscriptionStatus;

  if (hasAppSubscription) {
    return 'subscribedOnApp';
  }

  const hasWebSubscription = hasWebCoreSubscription || hasWebMaxSubscription;

  if (!hasWebSubscription) {
    return 'new';
  }

  const isSelectingSameTier =
    (selectedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE && hasWebCoreSubscription) ||
    (selectedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX && hasWebMaxSubscription);

  if (isSelectingSameTier) {
    const isRenewOff =
      selectedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX
        ? isWebMaxRenewOff
        : isWebCoreRenewOff;
    return isRenewOff ? 'renew' : 'alreadySubscribed';
  }

  if (selectedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX && hasWebCoreSubscription) {
    return 'upgrade';
  }

  if (selectedPlan === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE && hasWebMaxSubscription) {
    return isWebMaxRenewOff ? 'downgrade' : 'alreadyOnHigherPlan';
  }

  return 'new';
};
